// Helpers for calls to the report servers (/reports, /reports-archive) that sit behind the server-side login gate.
//
// The gate reads the SuperTokens access-token cookie that the SDK writes on this page's own hostname, so
// same-origin requests already carry it. These helpers never add an Authorization header. Their job is to keep
// that cookie fresh: Session.doesSessionExist() refreshes an expired token, and a 401 triggers one refresh + retry.
import Session from 'supertokens-web-js/recipe/session';
import { SuperTokensController as STC } from './SuperTokensController.js';

// Wait for SuperTokens.init, then let the SDK refresh an expired access token. The boolean is ignored.
async function touchSession() {
    await STC.ready;
    try {
        await Session.doesSessionExist();
    } catch (error) {
        console.log('gatedFetch: session check failed, sending the request anyway. Error: \n', error);
    }
}

async function refreshSession() {
    try {
        return await Session.attemptRefreshingSession();
    } catch (error) {
        console.log('gatedFetch: session refresh failed. Error: \n', error);
        return false;
    }
}

// fetch() with a fresh session cookie. Options are passed through untouched except credentials, which
// defaults to 'same-origin'. On HTTP 401: one refresh attempt, and if it succeeds one retry. Returns the last Response.
export async function gatedFetch(url, options = {}) {
    await touchSession();
    const finalOptions = { ...options, credentials: options.credentials || 'same-origin' };
    let response = await fetch(url, finalOptions);
    if (response.status === 401) {
        if (await refreshSession()) {
            response = await fetch(url, finalOptions);
        }
    }
    return response;
}

// XMLHttpRequest POST of a FormData with upload progress, mirroring the old Archiver.vue code.
// Handlers are optional and receive the raw XHR events. onLoad fires once, for the final attempt: when the first
// attempt returns 401, one refresh + resend is made and only the resend's load event reaches onLoad.
// Resolves with the XHR of the last attempt after it loads, aborts or errors.
export async function gatedUpload(url, formData, { onProgress, onLoad, onAbort } = {}) {
    await touchSession();
    return new Promise((resolve) => {
        const send = (isRetry) => {
            const ajax = new XMLHttpRequest();
            if (onProgress) {
                ajax.upload.addEventListener('progress', onProgress, false);
            }
            ajax.addEventListener('load', async (event) => {
                if (ajax.status === 401 && !isRetry && await refreshSession()) {
                    send(true);
                    return;
                }
                if (onLoad) {
                    onLoad(event);
                }
                resolve(ajax);
            }, false);
            ajax.addEventListener('abort', (event) => {
                if (onAbort) {
                    onAbort(event);
                }
                resolve(ajax);
            }, false);
            ajax.addEventListener('error', () => resolve(ajax), false);
            ajax.open('POST', url);
            ajax.withCredentials = true;
            ajax.send(formData);
        };
        send(false);
    });
}
